import { useCallback, useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

export type SortValue = string | number | null | undefined;

export type TableSort<Column extends string> = Readonly<{
  column: Column;
  direction: SortDirection;
}>;

export type SortValueGetters<Row, Column extends string> = Readonly<
  Record<Column, (row: Row) => SortValue>
>;

const compareSortValues = (left: string | number, right: string | number) =>
  typeof left === "number" && typeof right === "number"
    ? left - right
    : String(left).localeCompare(String(right), undefined, { numeric: true });

export const sortRows = <Row, Column extends string>({
  rows,
  sortValueGetters,
  sort,
}: {
  rows: readonly Row[];
  sortValueGetters: SortValueGetters<Row, Column>;
  sort: TableSort<Column> | null;
}): readonly Row[] => {
  if (sort == null) {
    return rows;
  }
  const getSortValue = sortValueGetters[sort.column];
  const directionSign = sort.direction === "asc" ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = getSortValue(left);
    const rightValue = getSortValue(right);
    // Empty values sort last in both directions.
    if (leftValue == null || rightValue == null) {
      return Number(leftValue == null) - Number(rightValue == null);
    }
    return directionSign * compareSortValues(leftValue, rightValue);
  });
};

type SortedRows<
  Row,
  Rows extends readonly Row[] | undefined,
> = Rows extends undefined ? undefined : readonly Row[];

export const useTableSort = <
  Row,
  Column extends string,
  Rows extends readonly Row[] | undefined,
>({
  rows,
  sortValueGetters,
  initialSort,
}: {
  rows: Rows;
  sortValueGetters: SortValueGetters<Row, Column>;
  initialSort: NoInfer<TableSort<Column>> | null;
}) => {
  const [sort, setSort] = useState<TableSort<Column> | null>(initialSort);

  const sortedRows = useMemo(
    () =>
      (rows && sortRows({ rows, sortValueGetters, sort })) as SortedRows<
        Row,
        Rows
      >,
    [rows, sortValueGetters, sort]
  );

  const toggleSort = useCallback((column: Column) => {
    setSort((current) => ({
      column,
      direction:
        current?.column === column && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  }, []);

  return { sortedRows, sort, toggleSort };
};
